import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { ISSUE_STATUS_META, SEVERITY_META, fmtDate } from './fleetUtils';

import AttachmentGallery from './AttachmentGallery';

const IssuePhotos = ({ photoIds }) => <AttachmentGallery photoIds={photoIds} />;

const DriverAssessmentForm = ({ issueId, onSaved }) => {
  const [form, setForm] = useState({ conclusion: 'a_determiner', comment: '', justification: '' });
  const save = async () => {
    try {
      await axios.post(`${API}/fleet/issues/${issueId}/driver-assessment`, form, { withCredentials: true });
      onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <div className="space-y-2">
      <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={form.conclusion}
        onChange={(e) => setForm({ ...form, conclusion: e.target.value })} data-testid="da-conclusion">
        <option value="a_determiner">À déterminer</option>
        <option value="non_liee">Non liée au livreur</option>
        <option value="potentiellement_liee">Potentiellement liée au livreur</option>
        <option value="liee">Liée au livreur</option>
        <option value="usure_normale">Usure normale</option>
        <option value="cause_exterieure">Cause extérieure ou tiers</option>
        <option value="cause_inconnue">Cause technique inconnue</option>
      </select>
      <Input placeholder={['potentiellement_liee', 'liee'].includes(form.conclusion) ? 'Commentaire OBLIGATOIRE' : 'Commentaire'}
        value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="da-comment" />
      <Button size="sm" onClick={save} data-testid="da-save-btn">Enregistrer l'avis</Button>
    </div>
  );
};

const IssueDetailDialog = ({ issueId, open, onClose, isFleetManager, onChanged }) => {
  const [data, setData] = useState(null);
  const [comment, setComment] = useState('');
  const [justif, setJustif] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/fleet/issues/${issueId}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur chargement anomalie');
      onClose();
    }
  };

  useEffect(() => { if (open && issueId) { setData(null); load(); } }, [open, issueId]);

  const act = async (path, body = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/fleet/issues/${issueId}/${path}`, body, { withCredentials: true });
      await load();
      onChanged?.();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action refusée');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const issue = data?.issue;
  const isOpen = issue && ['signalee', 'prise_en_charge', 'planifiee', 'en_reparation'].includes(issue.status);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="issue-detail-dialog">
        <DialogHeader>
          <DialogTitle>Anomalie — {issue?.plate_number} · {issue?.item_label}</DialogTitle>
        </DialogHeader>
        {!issue ? <div className="p-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <span className={`text-xs px-2 py-1 rounded font-bold ${SEVERITY_META[issue.severity]?.cls}`}>{SEVERITY_META[issue.severity]?.label}</span>
              <span className={`text-xs px-2 py-1 rounded ${ISSUE_STATUS_META[issue.status]?.cls}`}>{ISSUE_STATUS_META[issue.status]?.label}</span>
              {issue.immobilization_recommended && <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">Immobilisation recommandée par l'admin</span>}
            </div>
            <div className="text-sm space-y-1">
              <p><strong>Catégorie :</strong> {issue.category_label}</p>
              <p><strong>Description :</strong> {issue.description}</p>
              <p><strong>Signalée par :</strong> {issue.reported_by?.name} le {fmtDate(issue.created_at)}</p>
              {issue.target_date && <p><strong>Intervention prévue :</strong> {issue.target_date}</p>}
              {issue.assigned_to && <p><strong>Responsable :</strong> {issue.assigned_to.name}</p>}
              {issue.repair && <p><strong>Réparé par :</strong> {issue.repair.repaired_by} le {fmtDate(issue.repair.repaired_at)}</p>}
              {issue.resolution_justification && <p><strong>Justification :</strong> {issue.resolution_justification}</p>}
            </div>
            <IssuePhotos photoIds={issue.photos} />

            {isFleetManager && isOpen && (
              <div className="border rounded-lg p-3 space-y-2 bg-purple-50">
                <p className="text-sm font-semibold">Implication du livreur (confidentiel RH)</p>
                {issue.driver_assessment && (
                  <p className="text-xs">Dernier avis (v{issue.driver_assessment.version}) : <strong>{issue.driver_assessment.conclusion}</strong> — {issue.driver_assessment.driver_name || 'livreur non identifié'} — {issue.driver_assessment.comment || ''}</p>
                )}
                <DriverAssessmentForm issueId={issueId} onSaved={load} />
              </div>
            )}

            {isFleetManager && isOpen && (
              <div className="border rounded-lg p-3 space-y-2 bg-blue-50">
                <p className="text-sm font-semibold">Actions chef de parc</p>
                <div className="flex flex-wrap gap-2">
                  {issue.status === 'signalee' && (
                    <Button size="sm" disabled={busy} onClick={() => act('take-charge')} data-testid="issue-take-charge-btn">Prendre en charge</Button>
                  )}
                  {['signalee', 'prise_en_charge', 'planifiee'].includes(issue.status) && (
                    <Button size="sm" disabled={busy} variant="outline" onClick={() => act('start-repair')} data-testid="issue-start-repair-btn">Passer en réparation</Button>
                  )}
                  <Button size="sm" disabled={busy} className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => act('mark-repaired', { comment: comment || null })} data-testid="issue-mark-repaired-btn">✓ Réparé</Button>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="text-xs">Planifier l'intervention</label>
                    <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} data-testid="issue-plan-date" />
                  </div>
                  <Button size="sm" variant="outline" disabled={busy || !targetDate}
                    onClick={() => act('plan', { target_date: targetDate })} data-testid="issue-plan-btn">Planifier</Button>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="text-xs">Justification (non avérée / non réparable)</label>
                    <Input value={justif} onChange={(e) => setJustif(e.target.value)} placeholder="Justification obligatoire" data-testid="issue-justification" />
                  </div>
                  <Button size="sm" variant="outline" disabled={busy || !justif}
                    onClick={() => act('not-genuine', { justification: justif })} data-testid="issue-not-genuine-btn">Non avérée</Button>
                  <Button size="sm" variant="outline" disabled={busy || !justif}
                    onClick={() => window.confirm('Le véhicule passera en inactif. Continuer ?') && act('not-repairable', { justification: justif, final_state: 'inactif' })}
                    data-testid="issue-not-repairable-btn">Non réparable</Button>
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold mb-2">Historique</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(data.events || []).map((ev) => (
                  <div key={ev.event_id} className="text-xs border-l-2 border-blue-300 pl-2">
                    <p className="font-medium">{ev.action} {ev.old_status && ev.new_status ? `(${ev.old_status} → ${ev.new_status})` : ''}</p>
                    <p className="text-gray-500">{ev.actor?.name} ({ev.actor?.role}) — {fmtDate(ev.created_at)}</p>
                    {ev.comment && <p>{ev.comment}</p>}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Textarea rows={2} className="flex-1" placeholder="Ajouter un commentaire..."
                value={comment} onChange={(e) => setComment(e.target.value)} data-testid="issue-comment-input" />
              <Button size="sm" disabled={busy || !comment.trim()}
                onClick={async () => { await act('comment', { comment }); setComment(''); }}
                data-testid="issue-comment-btn">Envoyer</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IssueDetailDialog;
